import { useState, useEffect, useCallback } from "react";
import {
    fetchCoupons,
    createCoupon,
    updateCoupon,
    deleteCoupon,
    fetchAllStoresForCoupons, searchPromoterUsers
} from "../../service/api/orders";
import "../../css/Coupons.css";
import {SearchIcon, TrashIcon} from "lucide-react";

import { useNavigate } from "react-router-dom";

const DISCOUNT_TYPES = [
    { value: "PERCENTAGE", label: "Percentage (%)" },
    { value: "FIXED",      label: "Fixed Amount (₹)" },
];

const USER_TYPES = [
    { value: "ALL",           label: "Unlimited times for all users" },
    { value: "ONCE", label: "Once per user" },
    { value: "ONCENEW", label: "Once for new user for first order" },
    { value: "CUSTOM",        label: "Custom limit per user" },
];

const BENEFIT_TYPES = [
    { value: "ORDER_DISCOUNT", label: "Order Discount" },
    { value: "FREE_DELIVERY", label: "Free Full Delivery" },
    { value: "FREE_BASE_DELIVERY", label: "Free Base Delivery" },
];

const COMMISSION_TYPES = [
    { value: "PERCENTAGE", label: "Percentage (%) of subtotal" },
    { value: "FIXED",      label: "Fixed Amount (₹) per order" },
];

const emptyForm = {
    name: "", description: "", code: "", discount_type: "PERCENTAGE",
    discount: "", expiry_date: new Date().toISOString().split("T")[0],
    restaurant_id: [],  sponsored_restaurant_id: [], max_count: "", min_subtotal: "", max_discount: "",
    subtotal_message: "", user_type: "ALL", max_count_per_user: "", is_active: true,
    benefit_type: "ORDER_DISCOUNT",
    promoter_user_id: null, promoter_user_label: "", commission_type: "PERCENTAGE",
    commission_value: "", max_commission: "",
};

/* ── helpers ─────────────────────────────────────────────────────────────── */
function getStatusKey(isActive, expiry) {
    if (isActive !== 1) return "inactive";
    return "active";
}

function formatDateLabel(d) {
    if (!d) return null;
    const date = new Date(d);
    const diffDays = Math.round((new Date() - date) / 86400000);
    const dateStr = date.toISOString().split("T")[0]; // YYYY-MM-DD

    if (Math.abs(diffDays) < 1) return `Today (${dateStr})`;

    const absYears  = Math.floor(Math.abs(diffDays) / 365);
    const absMonths = Math.floor(Math.abs(diffDays) / 30);
    const absDays   = Math.abs(diffDays);

    let label;
    if (absYears >= 1)       label = `${absYears} year${absYears > 1 ? "s" : ""} ago`;
    else if (absMonths >= 1) label = `${absMonths} month${absMonths > 1 ? "s" : ""} ago`;
    else                     label = `${absDays} day${absDays > 1 ? "s" : ""} ago`;

    return `${label} ${"\n"} (${dateStr})`;
}

/* ── small presentational components ────────────────────────────────────── */
function StatusBadge({ isActive, expiry }) {
    const key = getStatusKey(isActive, expiry);
    return (
        <span className={`cp-status-badge cp-status-badge--${key}`}>
            <span className={`cp-status-dot cp-status-dot--${key}`} />
            {key.toUpperCase()}
        </span>
    );
}

function TypeBadge({ type }) {
    return (
        <span className={`cp-type-badge ${type === "PERCENTAGE" ? "cp-type-badge--pct" : "cp-type-badge--fixed"}`}>
            {type === "PERCENTAGE" ? "%" : "₹"} {type}
        </span>
    );
}

function Spinner() {
    return <span className="cp-spinner" />;
}

function Toast({ toast }) {
    if (!toast) return null;
    return (
        <div className={`cp-toast cp-toast--${toast.type}`}>
            {toast.type === "success" ? "✓ " : "✕ "}{toast.msg}
        </div>
    );
}

/* ── Field wrapper ───────────────────────────────────────────────────────── */
function Field({ label, required, error, span2, children }) {
    return (
        <div className={span2 ? "cp-form-grid--span2" : ""}>
            <label className="cp-field__label">
                {label} {required && <span className="cp-field__required">*</span>}
            </label>
            {children}
            {error && <div className="cp-field__error">{error}</div>}
        </div>
    );
}

function Input({ value, onChange, placeholder, type = "text", mono, error }) {
    return (
        <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className={`cp-input${mono ? " cp-input--mono" : ""}${error ? " cp-input--error" : ""}`}
        />
    );
}

function Select({ value, onChange, options }) {
    return (
        <select className="cp-select" value={value} onChange={e => onChange(e.target.value)}>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}

function PromoterSearch({ value, label, onChange }) {
    const [query, setQuery] = useState(label || "");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [searching, setSearching] = useState(false);

    useEffect(() => { setQuery(label || ""); }, [label]);

    useEffect(() => {
        if (query.trim().length < 2 || query === label) { setResults([]); return; }
        const t = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await searchPromoterUsers(query.trim());
                console.log(res);
                if (res.success) setResults(res.data || []);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [query]);

    return (
        <div className="cp-store-select-box" style={{ position: "relative" }}>
            <input
                className="cp-input"
                value={query}
                placeholder="Search user by name or phone…"
                onChange={e => { setQuery(e.target.value); setOpen(true); onChange(null, ""); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
            />
            {value && (
                <div className="cp-selected-stores">
                    <span className="cp-selected-store-chip">
                        {label}
                        <button type="button" onClick={() => { onChange(null, ""); setQuery(""); }}>×</button>
                    </span>
                </div>
            )}
            {open && query.trim().length >= 2 && (
                <div className="cp-select" style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
                    background: "#fff", maxHeight: 180, overflowY: "auto", border: "1px solid #ddd"
                }}>
                    {searching ? (
                        <div style={{ padding: 8 }}>Searching…</div>
                    ) : results.length === 0 ? (
                        <div style={{ padding: 8 }}>No users found</div>
                    ) : results.map(u => (
                        <div
                            key={u.id}
                            style={{ padding: 8, cursor: "pointer" }}
                            onMouseDown={() => { onChange(u.id, `${u.name} (${u.phone || "no phone"})`); setQuery(`${u.name} (${u.phone || "no phone"})`); setOpen(false); }}
                        >
                            {u.name} {u.phone ? `— ${u.phone}` : ""}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


/* ── Coupon modal (create + edit) ────────────────────────────────────────── */
function CouponModal({ open, onClose, initial, onSave, loading, stores = [] }) {
    const [form, setForm] = useState(emptyForm);
    const [err,  setErr]  = useState({});

    useEffect(() => {
        if (!open) return;
        setErr({});
        if (initial) {
            setForm({
                ...emptyForm,
                ...initial,
                expiry_date:  initial.expiry_date ? initial.expiry_date.split("T")[0] : emptyForm.expiry_date,
                restaurant_id: initial.restaurants?.map(r => r.id) ?? [],
                sponsored_restaurant_id: initial.sponsored_restaurants?.map(r => r.id) ?? [],
                is_active:    initial.is_active === true || initial.is_active === 1,
                promoter_user_id: initial.promoter_user_id ?? null,
                promoter_user_label: initial.promoter
                    ? `${initial.promoter.name} (${initial.promoter.phone || "no phone"})`
                    : "",
                commission_type: initial.commission_type || "PERCENTAGE",
                commission_value: initial.commission_value ?? "",
                max_commission: initial.max_commission ?? "",
            });
        } else {
            setForm(emptyForm);
        }
    }, [open, initial]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.name.trim())    e.name     = "Required";
        if (!form.code.trim())    e.code     = "Required";
        if (!form.discount && form.benefit_type === "ORDER_DISCOUNT")  e.discount  = "Required";
        if (!form.expiry_date)    e.expiry_date = "Required";
        if (!form.max_count)      e.max_count   = "Required";
        if (form.promoter_user_id && !form.commission_value) e.commission_value = "Required";
        setErr(e);
        console.log(e)
        return !Object.keys(e).length;
    };

    if (!open) return null;
    const isEdit = !!initial?.id;

    return (
        <div className="cp-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="cp-modal">

                {/* header */}
                <div className="cp-modal__header">
                    <div className="cp-modal__icon">🎟️</div>
                    <div className="cp-modal__title-wrap">
                        <div className="cp-modal__title">{isEdit ? "Edit Coupon" : "Add New Coupon"}</div>
                        <div className="cp-modal__sub">{isEdit ? `Editing: ${initial.name}` : "Fill in the details below"}</div>
                    </div>
                    <button className="cp-modal__close" onClick={onClose}>✕</button>
                </div>

                {/* body */}
                <div className="cp-modal__body">
                    <div className="cp-form-grid">

                        <Field label="Coupon Name" required error={err.name} span2>
                            <Input value={form.name} onChange={v => set("name", v)} placeholder="e.g. Summer Sale" error={err.name} />
                        </Field>

                        <Field label="Coupon Description" span2>
                            <Input value={form.description} onChange={v => set("description", v)} placeholder="Optional description" />
                        </Field>

                        <Field label="Coupon Code" required error={err.code}>
                            <Input value={form.code} onChange={v => set("code", v.toUpperCase())} placeholder="SAVE20" mono error={err.code} />
                        </Field>

                        <Field label="Coupon Benefit Type" required span2>
                            <Select
                                value={form.benefit_type}
                                onChange={v => set("benefit_type", v)}
                                options={BENEFIT_TYPES}
                            />
                        </Field>

                        {form.benefit_type === "ORDER_DISCOUNT" && (
                            <>
                                <Field label="Discount Type" required>
                                    <Select value={form.discount_type} onChange={v => set("discount_type", v)} options={DISCOUNT_TYPES} />
                                </Field>

                                <Field label={`Discount (${form.discount_type === "PERCENTAGE" ? "%" : "₹"})`} required error={err.discount}>
                                    <Input type="number" value={form.discount} onChange={v => set("discount", v)} placeholder="0" error={err.discount} />
                                </Field>

                                {form.discount_type === "PERCENTAGE" ? (
                                    <Field label="Max Discount (₹)">
                                        <Input type="number" value={form.max_discount} onChange={v => set("max_discount", v)} placeholder="Optional cap" />
                                    </Field>
                                ) : null}
                            </>
                        )}


                        <Field label="Expiry Date" required error={err.expiry_date} span2={form.discount_type !== "PERCENTAGE"}>
                            <Input type="date" value={form.expiry_date} onChange={v => set("expiry_date", v)} error={err.expiry_date} />
                        </Field>

                        <Field label="Max Uses (total)" required error={err.max_count}>
                            <Input type="number" value={form.max_count} onChange={v => set("max_count", v)} placeholder="e.g. 500" error={err.max_count} />
                        </Field>

                        <Field label="Min Subtotal (₹)">
                            <Input type="number" value={form.min_subtotal} onChange={v => set("min_subtotal", v)} placeholder="0" />
                        </Field>

                        <Field label="Subtotal Not Reached Message" span2>
                            <Input value={form.subtotal_message} onChange={v => set("subtotal_message", v)} placeholder="Add ₹X more to use this coupon" />
                        </Field>

                        <Field label="User Type" required span2={form.user_type !== "CUSTOM"}>
                            <Select value={form.user_type} onChange={v => set("user_type", v)} options={USER_TYPES} />
                        </Field>

                        {form.user_type === "CUSTOM" && (
                            <Field label="Max Uses Per User">
                                <Input type="number" value={form.max_count_per_user} onChange={v => set("max_count_per_user", v)} placeholder="1" />
                            </Field>
                        )}

                        <Field label="Select Stores" span2>
                            <div className="cp-store-select-box">
                                <select
                                    className="cp-select"
                                    value=""
                                    onChange={(e) => {
                                        const id = Number(e.target.value);
                                        if (!id) return;

                                        if (!form.restaurant_id.includes(id)) {
                                            set("restaurant_id", [...form.restaurant_id, id]);
                                        }
                                    }}
                                >
                                    <option value="">Select store to add</option>
                                    {stores
                                        .filter(store => !form.restaurant_id.includes(store.id))
                                        .map(store => (
                                            <option key={store.id} value={store.id}>
                                                {store.name}
                                            </option>
                                        ))}
                                </select>

                                <div className="cp-selected-stores">
                                    {form.restaurant_id.length === 0 ? (
                                        <span className="cp-empty-val">No stores selected</span>
                                    ) : (
                                        form.restaurant_id.map(id => {
                                            const store = stores.find(s => s.id === id);

                                            return (
                                                <span key={id} className="cp-selected-store-chip">
                                                    {store?.name || `Store #${id}`}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            set(
                                                                "restaurant_id",
                                                                form.restaurant_id.filter(storeId => storeId !== id)
                                                            );
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </Field>

                        <Field label="Admin Sponsored Stores" span2>
                            <div className="cp-store-select-box">
                                <select
                                    className="cp-select"
                                    value=""
                                    onChange={(e) => {
                                        const id = Number(e.target.value);
                                        if (!id) return;

                                        if (!form.sponsored_restaurant_id.includes(id)) {
                                            set("sponsored_restaurant_id", [
                                                ...form.sponsored_restaurant_id,
                                                id,
                                            ]);
                                        }
                                    }}
                                >
                                    <option value="">Select sponsored store to add</option>
                                    {stores
                                        .filter(store => !form.sponsored_restaurant_id.includes(store.id))
                                        .map(store => (
                                            <option key={store.id} value={store.id}>
                                                {store.name}
                                            </option>
                                        ))}
                                </select>

                                <div className="cp-selected-stores">
                                    {form.sponsored_restaurant_id.length === 0 ? (
                                        <span className="cp-empty-val">No sponsored stores selected</span>
                                    ) : (
                                        form.sponsored_restaurant_id.map(id => {
                                            const store = stores.find(s => s.id === id);

                                            return (
                                                <span key={id} className="cp-selected-store-chip cp-selected-store-chip--sponsored">
                                                    {store?.name || `Store #${id}`}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            set(
                                                                "sponsored_restaurant_id",
                                                                form.sponsored_restaurant_id.filter(storeId => storeId !== id)
                                                            );
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </Field>


                        <Field label="Influencer (optional)" span2>
                            <PromoterSearch
                                value={form.promoter_user_id}
                                label={form.promoter_user_label}
                                onChange={(id, label) => setForm(p => ({ ...p, promoter_user_id: id, promoter_user_label: label }))}
                            />
                        </Field>


                        {form.promoter_user_id && (
                            <>
                                <Field label="Commission Type" required>
                                    <Select value={form.commission_type} onChange={v => set("commission_type", v)} options={COMMISSION_TYPES} />
                                </Field>

                                <Field label={`Commission (${form.commission_type === "PERCENTAGE" ? "% of subtotal" : "₹ per order"})`} required error={err.commission_value}>
                                    <Input type="number" value={form.commission_value} onChange={v => set("commission_value", v)} placeholder="0" error={err.commission_value} />
                                </Field>

                                {form.commission_type === "PERCENTAGE" && (
                                    <Field label="Max Commission Cap (₹)">
                                        <Input type="number" value={form.max_commission} onChange={v => set("max_commission", v)} placeholder="Optional cap" />
                                    </Field>
                                )}
                            </>
                        )}

                        <Field label="Is Active?" span2>
                            <div className="cp-toggle-wrap">
                                <button
                                    type="button"
                                    className={`cp-toggle ${form.is_active ? "cp-toggle--on" : "cp-toggle--off"}`}
                                    onClick={() => set("is_active", !form.is_active)}
                                    aria-label="Toggle active"
                                >
                                    <span className={`cp-toggle__knob ${form.is_active ? "cp-toggle__knob--on" : "cp-toggle__knob--off"}`} />
                                </button>
                                <span className={`cp-toggle-label ${form.is_active ? "cp-toggle-label--on" : "cp-toggle-label--off"}`}>
                                    {form.is_active ? "Active" : "Inactive"}
                                </span>
                            </div>
                        </Field>

                    </div>
                </div>

                {/* footer */}
                <div className="cp-modal__footer">
                    <button className="cp-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="cp-save-btn" onClick={() => { if (validate()) onSave(form); }} disabled={loading}>
                        {loading ? <><Spinner /> Saving…</> : isEdit ? "✓ Update Coupon" : "+ Create Coupon"}
                    </button>
                </div>

            </div>
        </div>
    );
}

/* ── Delete confirm modal ────────────────────────────────────────────────── */
function DeleteConfirm({ open, coupon, onClose, onConfirm, loading }) {
    if (!open) return null;
    return (
        <div className="cp-del-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="cp-del-modal">
                <div className="cp-del-modal__icon"><TrashIcon/></div>
                <div className="cp-del-modal__title">Delete Coupon?</div>
                <div className="cp-del-modal__desc">
                    This will permanently delete <strong>{coupon?.name}</strong>{" "}
                    (<code className="cp-del-modal__code">{coupon?.code}</code>). This action cannot be undone.
                </div>
                <div className="cp-del-modal__actions">
                    <button className="cp-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="cp-confirm-del-btn" onClick={onConfirm} disabled={loading}>
                        {loading ? "Deleting…" : "Yes, Delete"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function Coupons() {
    const [coupons,      setCoupons]      = useState([]);
    const [meta,         setMeta]         = useState({ total: 0, current_page: 1, last_page: 1, per_page: 15 });
    const [loading,      setLoading]      = useState(true);
    const [search,       setSearch]       = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page,         setPage]         = useState(1);



    const [modal,    setModal]    = useState({ open: false, coupon: null });
    const [delModal, setDelModal] = useState({ open: false, coupon: null });
    const [saving,   setSaving]   = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [toast,    setToast]    = useState(null);

    const [stores, setStores] = useState([]);
    const navigate = useNavigate();

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const loadCoupons = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const res = await fetchCoupons({ page: p, per_page: 15 });
            if (res.success) {
                setCoupons(res.data);
                setMeta(res.meta);
            }
        } catch {
            showToast("Failed to load coupons", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadStores = useCallback(async () => {
        try {
            if(stores?.length > 0) return;
            const res = await fetchAllStoresForCoupons();
            if (res.success) {
                setStores(res.data || []);
            }
        } catch {
            showToast("Failed to load stores", "error");
        }
    }, []);

    useEffect(() => { loadCoupons(page); loadStores(); }, [page]);


    /* client-side filter on fetched page */
    const filtered = coupons.filter(c => {
        const q = search.toLowerCase();
        const matchSearch = !q || c.name?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q);
        const key = getStatusKey(c.is_active, c.expiry_date);
        const matchStatus = statusFilter === "all" || key === statusFilter;
        return matchSearch && matchStatus;
    });

    /* save handler — create or update */
    const handleSave = async (form) => {
        setSaving(true);
        try {
            const isEdit = !!modal.coupon?.id;
            const payload = isEdit ? { ...form, id: modal.coupon.id } : form;
            const res = isEdit ? await updateCoupon(payload) : await createCoupon(payload);
            if (res.success) {
                showToast(isEdit ? "Coupon updated!" : "Coupon created!");
                setModal({ open: false, coupon: null });
                loadCoupons(page);
            } else {
                showToast(res.message || "Something went wrong", "error");
            }
        } catch {
            showToast("Network error", "error");
        } finally {
            setSaving(false);
        }
    };

    /* delete handler */
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await deleteCoupon(delModal.coupon.id);
            if (res.success) {
                showToast("Coupon deleted");
                setDelModal({ open: false, coupon: null });
                loadCoupons(page);
            } else {
                showToast(res.message || "Failed to delete", "error");
            }
        } catch {
            showToast("Network error", "error");
        } finally {
            setDeleting(false);
        }
    };

    /* pagination pages array */
    const pageRange = Array.from({ length: meta.last_page }, (_, i) => i + 1)
        .slice(Math.max(0, page - 3), Math.min(meta.last_page, page + 2));

    return (
        <>
            <div className="cp-page">

                {/* ── Header ── */}
                <div className="cp-header">
                    <div>
                        <h1 className="cp-header__title">🎟️ Coupons</h1>
                        <p className="cp-header__sub">{meta.total} coupon{meta.total !== 1 ? "s" : ""} total</p>
                    </div>
                    <button className="cp-add-btn" onClick={() => setModal({ open: true, coupon: null })}>
                        <span className="cp-add-btn__icon">+</span> Add New Coupon
                    </button>
                </div>

                {/* ── Table card ── */}
                <div className="cp-card">

                    {/* toolbar */}
                    <div className="cp-toolbar">
                        <div className="cp-search-wrap">
                            <span className="cp-search-icon">
                                <SearchIcon />
                            </span>
                            <input
                                className="cp-search-input"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search by name or code…"
                            />
                        </div>
                        <select
                            className="cp-status-select"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="expired">Expired</option>
                            <option value="inactive">Inactive</option>
                        </select>
                        <span className="cp-count-label">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
                    </div>

                    {/* table */}
                    <div className="cp-table-wrap">
                        <table className="cp-table">
                            <thead className="cp-thead">
                            <tr>
                                {["Name","Code","Store(s)","Type","Discount", "Promoter", "Status","Usage","Expiry","Min Sub.","Max Disc.",""].map((h, i) => (
                                    <th key={i} className={`cp-th${i === 11 ? " cp-th--right" : ""}`}>{h}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="cp-tbody">
                            {loading ? (
                                Array.from({ length: 7 }).map((_, i) => (
                                    <tr key={i}>
                                        {Array.from({ length: 11 }).map((_, j) => (
                                            <td key={j} className="cp-td">
                                                <div className="cp-skeleton" style={{ width: j === 10 ? 60 : "70%" }} />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={11}>
                                        <div className="cp-empty">
                                            <div className="cp-empty__icon">🎟️</div>
                                            <div className="cp-empty__title">No coupons found</div>
                                            <div className="cp-empty__sub">Try a different filter or create a new coupon</div>
                                        </div>
                                    </td>
                                </tr>
                            ) : filtered.map(c => (
                                <tr key={c.id}>
                                    <td className="cp-td">
                                        <div className="cp-name">{c.name}</div>
                                        {c.description && <div className="cp-name-desc">{c.description}</div>}
                                    </td>
                                    <td className="cp-td">
                                        <code className="cp-code-badge">{c.code}</code>
                                    </td>
                                    <td className="cp-td">
                                        {c.restaurants?.length > 1
                                            ? <span className="cp-store-badge">{c.restaurants.length} Stores</span>
                                            : c.restaurants?.length === 1
                                                ? <span className="cp-store-name">{c.restaurants[0]?.name}</span>
                                                : <span className="cp-empty-val">—</span>
                                        }
                                    </td>
                                    <td className="cp-td">
                                        {c.benefit_type === "FREE_DELIVERY" ? (
                                            <span className="cp-type-badge cp-type-badge--free">
            🚚 FREE DELIVERY
        </span>
                                        ) : c.benefit_type === "FREE_BASE_DELIVERY" ? (
                                            <span className="cp-type-badge cp-type-badge--free">
            🚚 FREE BASE
        </span>
                                        ) : (
                                            <TypeBadge type={c.discount_type}/>
                                        )}
                                    </td>

                                    <td className="cp-td">
                                    <span className="cp-discount-val">
                                        {c.benefit_type === "FREE_DELIVERY"
                                            ? "Full Delivery Free"
                                            : c.benefit_type === "FREE_BASE_DELIVERY"
                                                ? "Base Delivery Free"
                                                : c.discount_type === "PERCENTAGE"
                                                    ? `${c.discount}%`
                                                    : `₹${c.discount}`}
                                    </span>
                                    </td>
                                    <td className="cp-td">
                                        {c.promoter
                                            ? <span className="cp-store-name">{c.promoter.name}</span>
                                            : <span className="cp-empty-val">—</span>}
                                    </td>
                                    <td className="cp-td"><StatusBadge isActive={c.is_active} expiry={c.expiry_date}/>
                                    </td>
                                    <td className="cp-td">
                                        <span className="cp-usage-val">{c.count ?? 0}</span>
                                    </td>
                                    <td className="cp-td">
                                        {c.expiry_date ? (
                                            <>
                                                <div
                                                    className={`cp-expiry-status ${new Date(c.expiry_date) < new Date() ? "cp-expiry-status--expired" : "cp-expiry-status--active"}`}>
                                                    {new Date(c.expiry_date) < new Date() ? "EXPIRED" : "ACTIVE"}
                                                </div>
                                                <div className="cp-expiry-date">{formatDateLabel(c.expiry_date)}</div>
                                            </>
                                        ) : <span className="cp-empty-val">—</span>}
                                    </td>
                                    <td className="cp-td">
                                            <span className="cp-subtotal-val">
                                                {c.min_subtotal > 0 ? `₹${c.min_subtotal}` :
                                                    <span className="cp-empty-val">—</span>}
                                            </span>
                                    </td>
                                    <td className="cp-td">
                                            <span className="cp-subtotal-val">
                                                {c.max_discount ? `₹${c.max_discount}` :
                                                    <span className="cp-empty-val">NA</span>}
                                            </span>
                                    </td>
                                    <td className="cp-td cp-td--right">
                                        <div className="cp-actions">
                                            <button className="cp-edit-btn"
                                                    onClick={() => navigate(`/promotions/coupons/${c.id}/stats`)}>View
                                            </button>
                                            <button className="cp-edit-btn"
                                                    onClick={() => setModal({open: true, coupon: c})}>Edit
                                            </button>
                                            <button className="cp-del-btn"
                                                    onClick={() => setDelModal({open: true, coupon: c})}
                                                    title="Delete">🗑
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* pagination */}
                    {meta.last_page > 1 && (
                        <div className="cp-pagination">
                            <span className="cp-pagination__info">
                                Page {meta.current_page} of {meta.last_page} — {meta.total} coupons
                            </span>
                            <div className="cp-pagination__pages">
                                <button
                                    className="cp-page-btn"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                >‹</button>
                                {pageRange.map(p => (
                                    <button
                                        key={p}
                                        className={`cp-page-btn${p === page ? " cp-page-btn--active" : ""}`}
                                        onClick={() => setPage(p)}
                                    >{p}</button>
                                ))}
                                <button
                                    className="cp-page-btn"
                                    onClick={() => setPage(p => Math.min(meta.last_page, p + 1))}
                                    disabled={page === meta.last_page}
                                >›</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* modals */}
            <CouponModal
                open={modal.open}
                onClose={() => setModal({ open: false, coupon: null })}
                initial={modal.coupon}
                onSave={handleSave}
                loading={saving}
                stores={stores}
            />
            <DeleteConfirm
                open={delModal.open}
                coupon={delModal.coupon}
                onClose={() => setDelModal({ open: false, coupon: null })}
                onConfirm={handleDelete}
                loading={deleting}
            />
            <Toast toast={toast} />
        </>
    );
}